import { useState } from 'react';
import { FaPaperPlane } from 'react-icons/fa';
import {
  IoRocketSharp,
  IoPeopleOutline,
} from 'react-icons/io5';
import { AiOutlineApi } from 'react-icons/ai';
import { BiSupport } from 'react-icons/bi';
import { GoRocket } from 'react-icons/go';
import { RiRocket2Fill } from 'react-icons/ri';

import { Modal } from '@zeeve-platform/modal-component';
import PlanCard, { Listitem } from './planCard';
import Urls from '../../utils/urls';
import apiBackendInstance from '../../utils/axios';
import CustomLoader from '../common/loader';

export type PlansType = string;
export type Plan = { code: string; price: number };

export type Addon = Plan;
export type PlanData = {
  code: string;
  price: number;
}[];
interface PlansHandleType {
  selectedPlan: PlansType;
  setSelectedPlan: (
    plan: string
  ) => void;
}

export type PlansProps = PlansHandleType & { planData: PlanData };

const Plans = ({
  selectedPlan,
  setSelectedPlan,
  planData,
}: PlansProps) => {
  const [showModal, setShowModal] = useState(false);
  const [redirectionLink, setRedirectionLink] = useState('#');
  const [status, setStatus] = useState(false);
  const [apiResponse, setApiResponse] = useState('');
  const [btnDisable, setBtnDisable] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [selectedCode, setSelectedCode] = useState('');

  /** local state to store feature list of developer plan  */
  const planOptions = [
    {
      name: 'Developer',
      description:
        'If you are learning web3 or if you are a tinkerer or trader or just exploring blockchain.',
      code: Urls.getItemCode('DEVELOPER'),
      price: '10 million requests per month',
      supportTime: 'Community support',
      apiRateLimit: '25 Requests / sec',
    },
    {
      name: 'Launch',
      description:
        'Building a small project or a POC. Start off your business implementations with extended offerings.',
      code: Urls.getItemCode('LAUNCH'),
      price: '20 million requests per month',
      supportTime: '24 hours support response time',
      apiRateLimit: '100 Requests / sec',
    },
    {
      name: 'Build',
      description:
        'Power your web3 projects with this plan to grow your business and reach out to a larger audience.',
      code: Urls.getItemCode('BUILD'),
      price: '50 million requests per month',
      supportTime: '8-12 hours support response time',
      apiRateLimit: '150 Requests / sec',
    },
    {
      name: 'Growth',
      description:
        'This plan is for you if you are running an enterprise project and serving out to the global market.',
      code: Urls.getItemCode('GROWTH'),
      price: '120 million requests per month',
      supportTime: '8-12 hours support response time',
      apiRateLimit: '300 Requests / sec',
    },
  ];
  const [developerPlanFeatureList] = useState<Array<Listitem>>([
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 18 }}> 10 Million</span>
          <span className="fw-bold" style={{ fontSize: 15 }}> API Units</span>
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 15 }}> 1 RPC Endpoint</span>
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 15 }}>25 Requests / sec</span>
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 15 }}>Blockchain Data</span>
          <br />
          <span className="fw-bold px-1" style={{ fontSize: 15, marginLeft: '20px' }}>- 25 API Units / sec</span>
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },

    {
      name: 'Community Support',
      icon: <IoPeopleOutline className="me-1" />,
      className: 'text-muted fs-7',
    },
  ]);

  /** local state to store feature list of Launch plan  */
  const [launchPlanFeatureList] = useState<Array<Listitem>>([
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 18 }}> 20 Million</span>
          <span className="fw-bold" style={{ fontSize: 15 }}> API Units</span>
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 15 }}> 10 RPC Endpoints</span>
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 15 }}>100 Requests / sec</span>
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 15 }}>Blockchain Data</span>
          <br />
          <span className="fw-bold px-1" style={{ fontSize: 15, marginLeft: '20px' }}>- 40 API Units / sec</span>
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },
    {
      name: (
        <span>
          <span className="fw-bold text-primary">24 Hours </span>
          {' '}
          Support response time
        </span>
      ),
      icon: <BiSupport className="me-1 text-primary" />,
      className: 'text-muted fs-7',
    },
  ]);
  /** local state to store feature list of standard plan  */
  const [buildPlanFeatureList] = useState<Array<Listitem>>([
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 18 }}> 50 Million</span>
          <span className="fw-bold" style={{ fontSize: 15 }}> API Units</span>
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 15 }}> 10 RPC Endpoints</span>
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 15 }}> 150  Requests / sec</span>
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 15 }}>Blockchain Data</span>
          <br />
          <span className="fw-bold px-1" style={{ fontSize: 15, marginLeft: '20px' }}>- 70 API Units / sec</span>
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },
    {
      name: (
        <span>
          <span className="fw-bold text-primary">8-12 Hours </span>
          {' '}
          Support response time
        </span>
      ),
      icon: <BiSupport className="me-1 text-primary" />,
      className: 'text-muted fs-7',
    },
  ]);
  /** local state to store feature list of standard plan  */
  const [growthPlanFeatureList] = useState<Array<Listitem>>([
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 18 }}> 120 Million</span>
          <span className="fw-bold" style={{ fontSize: 15 }}> API Units</span>
          <br />
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 15 }}> 20 RPC Endpoints</span>
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 15 }}>300 Requests / sec</span>
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },
    {
      name: (
        <span>
          <span className="fw-bold px-1" style={{ fontSize: 15 }}>Blockchain Data</span>
          <br />
          <span className="fw-bold px-1" style={{ fontSize: 15, marginLeft: '20px' }}>- 120 API Units / sec</span>
        </span>
      ),
      icon: <AiOutlineApi className="me-1" />,
      className: 'text-muted fs-7',
    },
    {
      name: (
        <span>
          <span className="fw-bold text-primary">8-12 Hours </span>
          {' '}
          Support response time
        </span>
      ),
      icon: <BiSupport className="me-1 text-primary" />,
      className: 'text-muted fs-7',
    },
  ]);
  const getCostByCode = (code: string) => {
    if (planData) {
      const isCodePresent = planData.find((o) => o.code === code);
      if (isCodePresent) {
        return isCodePresent?.price;
      }
      return 0;
    }
    return 0;
  };

  const subscribe = (code: string) => {
    setSelectedCode(code);
    setShowLoader(true);
    const data = {
      plan: {
        code,
        quantity: 1,
      },
      serviceType: 'rpc',
    };
    apiBackendInstance.post(Urls.getSubscription(), { ...data },
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },

      })
      .then((response) => {
        if (response?.data?.data?.message) {
          setApiResponse(response.data.data.message);
        }
        if (response.data.success) {
          if (response?.data?.data?.redirectionUrl) {
            setStatus(true);
            setRedirectionLink(response.data.data.redirectionUrl);
          }
          setShowModal(true);
        }
        setBtnDisable(false);
        setShowLoader(false);
      }).catch((err) => {
        const errMsgRes = err && err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Request Failed';
        setApiResponse(`Error: ${errMsgRes}`);
        setShowModal(true);
        setStatus(false);
        setRedirectionLink('');
        setBtnDisable(false);
        setShowLoader(false);
      });
  };

  return (
    <div className="row text-center gx-4  gy-4">
      <div className="col-lg-3 col-md-6 col-sm-12">
        <CustomLoader showLoader={showLoader} />

        <PlanCard
          onClick={(event: any) => {
            event.preventDefault();
            setSelectedPlan(planOptions[0].code);
            if (selectedPlan === planOptions[0].code) setSelectedPlan('none');
          }}
          isSelected={selectedPlan === planOptions[0].code}
          planCode={planOptions[0].code}
          icon={<FaPaperPlane size={50} className="mt-5 mb-4 text-muted" />}
          heading="developer"
          subHeading="For Experimenting"
          description={planOptions[0].description}
          price={{
            unit: '$',
            value: `${(getCostByCode(planOptions[0].code) || 0)}`,
            period: 'MONTHLY',
          }}
          featureList={developerPlanFeatureList}
          subscribe={subscribe}
        />
      </div>
      <div className="col-lg-3 col-md-6 col-sm-12">
        <PlanCard
          onClick={(event: any) => {
            event.preventDefault();
            setSelectedPlan(planOptions[1].code);
            if (selectedPlan === planOptions[1].code) setSelectedPlan('none');
          }}
          description={planOptions[1].description}
          isSelected={selectedPlan === planOptions[1].code}
          planCode={planOptions[1].code}
          icon={<RiRocket2Fill size={55} className="mt-5 mb-3 text-primary" />}
          heading="launch"
          subHeading="For Start off Business"
          price={{
            unit: '$',
            value: `${(getCostByCode(planOptions[1].code) || 0)}`,
            period: 'MONTHLY',
          }}
          featureList={launchPlanFeatureList}
          subscribe={subscribe}
        />
      </div>
      <div className="col-lg-3 col-md-6 col-sm-12">
        <PlanCard
          onClick={(event: any) => {
            event.preventDefault();
            setSelectedPlan(planOptions[2].code);
            if (selectedPlan === planOptions[2].code) setSelectedPlan('none');
          }}
          isSelected={selectedPlan === planOptions[2].code}
          planCode={planOptions[2].code}
          description={planOptions[2].description}
          icon={<GoRocket size={50} className="mt-5 mb-3 text-primary" />}
          heading="build"
          subHeading="For Scaling Business"
          price={{
            unit: '$',
            value: `${(getCostByCode(planOptions[2].code) || 0)}`,
            period: 'MONTHLY',
          }}
          featureList={buildPlanFeatureList}
          subscribe={subscribe}
        />
      </div>
      <div className="col-lg-3 col-md-6 col-sm-12">
        <PlanCard
          onClick={(event: any) => {
            event.preventDefault();
            setSelectedPlan(planOptions[3].code);
            if (selectedPlan === planOptions[3].code) setSelectedPlan('none');
          }}
          isSelected={selectedPlan === planOptions[3].code}
          planCode={planOptions[3].code}
          icon={<IoRocketSharp size={52} className="mt-5 mb-3 text-danger" />}
          heading="growth"
          description={planOptions[3].description}
          subHeading="For Growing Enterprise"
          price={{
            unit: '$',
            value: `${(getCostByCode(planOptions[3].code) || 0)}`,
            period: 'MONTHLY',
          }}
          featureList={growthPlanFeatureList}
          subscribe={subscribe}
        />
      </div>
      <Modal
        show={showModal}
        onHide={() => { setShowModal(false); }}
        title={`Purchasing ${planOptions.filter((e) => e.code === selectedCode)?.[0]?.name} Plan`}
        confirmBtnText="Continue"
        visibleFooterCloseButton
        visibleFooter
        visibleConfirmBtn={status}
        visibleHeaderCloseIcon={false}
        disableConfirmBtn={btnDisable}
        onConfirm={() => window.open(redirectionLink, '_self')}
      >
        <p className="status text-center mt-3" id="modal_status">{apiResponse}</p>
      </Modal>
    </div>
  );
};

export default Plans;
